import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

interface SettingType {
  IGST: number;
  CGST: number;
  SGST: number;
  InvoiceSN: string;
}

const Settings: React.FC = () => {
  const [setting, setSetting] = useState<SettingType>({
    IGST: 0,
    CGST: 0,
    SGST: 0,
    InvoiceSN: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch settings from the server
  useEffect(() => {
    setLoading(true);
    setError('');
    axios
      .get(`${API_URL}/auth/setting`)
      .then((res) => {
        setSetting({
          IGST: res.data.IGST ?? 0,
          CGST: res.data.CGST ?? 0,
          SGST: res.data.SGST ?? 0,
          InvoiceSN: res.data.InvoiceSN ?? '',
        });
      })
      .catch(() => setError('Unable to load settings.'))
      .finally(() => setLoading(false));
  }, []);

  // Handle input changes for form fields
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSetting((prev) => ({
      ...prev,
      [name]: name === 'InvoiceSN' ? value : Number(value),
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await axios.put(`${API_URL}/auth/setting`, setting);
      setSuccess('Settings updated successfully!');
    } catch (err) {
      setError('Failed to update settings.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto p-5">
      <h1 className="text-2xl font-bold mb-4">Settings</h1>
      {loading ? (
        <div>Loading...</div>
      ) : (
        <form className="space-y-4" onSubmit={handleSubmit}>
          {error && <div className="text-red-600">{error}</div>}
          {success && <div className="text-green-600">{success}</div>}
          <div>
            <label className="block font-medium mb-1" htmlFor="IGST">
              IGST (%)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              id="IGST"
              name="IGST"
              value={setting.IGST}
              onChange={handleInputChange}
              className="w-full border rounded px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="block font-medium mb-1" htmlFor="CGST">
              CGST (%)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              id="CGST"
              name="CGST"
              value={setting.CGST}
              onChange={handleInputChange}
              className="w-full border rounded px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="block font-medium mb-1" htmlFor="SGST">
              SGST (%)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              id="SGST"
              name="SGST"
              value={setting.SGST}
              onChange={handleInputChange}
              className="w-full border rounded px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="block font-medium mb-1" htmlFor="InvoiceSN">
              Invoice Serial Number (Prefix)
            </label>
            <input
              type="text"
              id="InvoiceSN"
              name="InvoiceSN"
              value={setting.InvoiceSN}
              onChange={handleInputChange}
              className="w-full border rounded px-3 py-2"
              required
              placeholder="e.g. INV-2024-"
              maxLength={20}
            />
          </div>
          <button
            type="submit"
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition"
            disabled={submitting}
          >
            {submitting ? 'Updating...' : 'Update Settings'}
          </button>
        </form>
      )}
    </div>
  );
};

export default Settings;